import React, { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import CompetitorPage from "./pages/CompetitorPage";
import Home from "./pages/Home";
import Login from "./pages/Login";
import { CompetitorProvider } from "./context/competitorContext";

// Auth Context
const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  console.log('🟣 AuthProvider initialized, current user:', user);

  const login = (userData) => {
    console.log('🟣 Login function called with userData:', userData);
    setUser(userData);
    localStorage.setItem("authUser", JSON.stringify(userData));
    localStorage.setItem("authToken", userData.token);
    console.log('🟣 Data stored in localStorage and user state updated');
  };

  const logout = () => {
    console.log('🟣 Logout function called');
    setUser(null);
    localStorage.removeItem("authUser");
    localStorage.removeItem("authToken");
    console.log('🟣 User logged out, localStorage cleared');
  };

  useEffect(() => {
    console.log('🟣 AuthProvider useEffect running');
    const storedUser = localStorage.getItem("authUser");
    console.log('🟣 Stored user data:', storedUser);
    
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      const now = Date.now();
      console.log('🟣 Checking session expiry:', {
        currentTime: now,
        expiryTime: parsedUser.expiry,
        isValid: parsedUser.expiry && now < parsedUser.expiry
      });

      if (parsedUser.expiry && now < parsedUser.expiry) {
        console.log('🟣 Session valid, restoring user:', parsedUser);
        setUser(parsedUser);
      } else {
        console.warn("❌ Session expired, logging out.");
        logout();
      }
    } else {
      console.log('🟣 No stored user data found');
    }
    
    setIsInitialized(true);
  }, []);

  // Don't render anything until we've checked authentication
  if (!isInitialized) {
    console.log('🟣 Auth still initializing...');
    return null;
  }

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ element }) {
  const { user } = useAuth();
  const location = window.location.pathname;
  console.log('🟡 ProtectedRoute check:', {
    hasUser: !!user,
    userData: user,
    path: location
  });

  // If we're on the login page and authenticated, redirect to home
  if (location === '/login' && user) {
    console.log('🟡 Authenticated user on login page, redirecting to home');
    return <Navigate to="/" replace />;
  }

  // If not authenticated and not on login page, redirect to login
  if (!user && location !== '/login') {
    console.log('🟡 Unauthenticated user, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  // If authenticated and on a protected route, render the element
  if (user && location !== '/login') {
    console.log('🟡 Authenticated user, rendering protected content');
    return element;
  }

  // For all other cases (like login page when not authenticated), render normally
  return element;
}

function App() {
  return (
    <AuthProvider>
      <CompetitorProvider>
        <Router>
          <Routes>
            <Route path="/" element={<ProtectedRoute element={<Home />} />} />
            <Route path="/competitor/:competitorName" element={<ProtectedRoute element={<CompetitorPage />} />} />
            <Route path="/login" element={<ProtectedRoute element={<Login />} />} />
          </Routes>
        </Router>
      </CompetitorProvider>
    </AuthProvider>
  );
}

export default App;